import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import {
  Button,
  Checkbox,
  DatePickerComp,
  Input,
  Select,
  Textarea,
  TimePickerComp,
} from "../Form";
import { BiChevronDown, BiPlus } from "react-icons/bi";
import { servicesData, sortsDatas } from "../Datas";
import { HiOutlineCheckCircle } from "react-icons/hi";
import { toast } from "react-hot-toast";
import PatientMedicineServiceModal from "./PatientMedicineServiceModal";
import axios from "axios";
import { BaseUrl } from "../../BaseUrl.js";

function AddAppointmentModal({ closeModal, isOpen, datas }) {
  const [services, setServices] = useState(servicesData[0]);
  const [startDate, setStartDate] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [status, setStatus] = useState(sortsDatas.status[0]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [purposeOfVisit, setPurposeOfVisit] = useState(servicesData[0]);
  const [description, setDescription] = useState("");
  const [shares, setShares] = useState({
    email: false,
    sms: false,
    whatsapp: false,
  });
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch doctors
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await axios.get(
          `${BaseUrl}/facility/getAllProfessionalUsers`,
          {
            withCredentials: true,
          }
        );
        if (response.data?.data) {
          const docs = response.data.data.map((d) => ({
            ...d,
            name: d.fullName,
          }));
          setDoctors(docs);
          if (docs.length > 0) setSelectedDoctor(docs[0]);
        }
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    };
    if (isOpen) fetchDoctors();
  }, [isOpen]);

  // on change share
  const onChangeShare = (e) => {
    setShares({ ...shares, [e.target.name]: e.target.checked });
  };

  // set data for editing
  useEffect(() => {
    if (datas?.title) {
      // Mapping for edit mode would go here, but for now focusing on creation
      // setServices(datas?.service);
      setStartDate(new Date(datas?.dateOfVisit || new Date()));
      // setStartTime(datas?.start);
      // setEndTime(datas?.end);
      // setShares(datas?.shareData);
      setDescription(datas?.message || "");
    }
  }, [datas]);

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setOpen(false);
  };

  const handleSave = async () => {
    if (!selectedPatient) {
      toast.error("Please select a patient");
      return;
    }
    if (!selectedDoctor) {
      toast.error("Please select a doctor");
      return;
    }

    setIsLoading(true);
    try {
      // Format time as HH:mm
      const formatTime = (date) => {
        return date.toTimeString().slice(0, 5);
      };

      const payload = {
        userId: selectedPatient._id,
        doctorId: selectedDoctor._id, // Ideally should be passed if multiple doctors
        purposeOfVisit: purposeOfVisit.name,
        dateOfVisit: startDate,
        startTime: formatTime(startTime),
        endTime: formatTime(endTime),
        description: description,
        shareWithPatient: shares,
        status: status.name,
      };

      await axios.post(`${BaseUrl}/facility/appointments`, payload, {
        withCredentials: true,
      });

      toast.success("Appointment created successfully!");
      closeModal();
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error(
        error.response?.data?.message || "Failed to create appointment"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      closeModal={closeModal}
      isOpen={isOpen}
      title={datas?.title ? "Edit Appointment" : "New Appointment"}
      width={"max-w-3xl"}
    >
      {open && (
        <PatientMedicineServiceModal
          closeModal={() => setOpen(false)}
          isOpen={open}
          patient={true}
          onSelect={handlePatientSelect}
        />
      )}
      <div className='flex-colo gap-6'>
        <div className='grid w-full items-center gap-4 sm:grid-cols-12'>
          <div className='sm:col-span-12'>
            <label className='mb-1 block text-sm font-medium text-black'>
              Patient Name
            </label>
            <div
              onClick={() => setOpen(true)}
              className={`flex w-full cursor-pointer items-center justify-between rounded-lg border px-4 py-4 text-sm transition-colors ${selectedPatient ? "border-subMain bg-subMain/5 text-black" : "border-border text-textGray hover:border-subMain"}`}
            >
              <span className={selectedPatient ? "font-medium" : ""}>
                {selectedPatient
                  ? selectedPatient.fullName
                  : "Select a patient..."}
              </span>
              <div className='flex items-center gap-2'>
                {!selectedPatient && (
                  <span className='rounded bg-subMain/10 px-2 py-1 text-xs text-subMain'>
                    Search
                  </span>
                )}
                <BiPlus
                  className={`text-xl ${selectedPatient ? "text-subMain" : "text-subMain"}`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='grid w-full gap-4 sm:grid-cols-2'>
          <div className='flex w-full flex-col gap-3'>
            <p className='text-sm text-black'>Purpose of visit</p>
            <Select
              selectedPerson={purposeOfVisit}
              setSelectedPerson={setPurposeOfVisit}
              datas={servicesData}
            >
              <div className='flex-btn w-full rounded-lg border border-border p-4 text-sm font-light text-textGray focus:border focus:border-subMain'>
                {purposeOfVisit.name} <BiChevronDown className='text-xl' />
              </div>
            </Select>
          </div>
          {/* date */}
          <DatePickerComp
            label='Date of visit'
            startDate={startDate}
            onChange={(date) => setStartDate(date)}
          />
        </div>

        <div className='grid w-full gap-4 sm:grid-cols-2'>
          <TimePickerComp
            label='Start time'
            startDate={startTime}
            onChange={(date) => setStartTime(date)}
          />
          <TimePickerComp
            label='End time'
            startDate={endTime}
            onChange={(date) => setEndTime(date)}
          />
        </div>

        {/* status && doctor */}
        <div className='grid w-full gap-4 sm:grid-cols-2'>
          <div className='flex w-full flex-col gap-3'>
            <p className='text-sm text-black'>Doctor</p>
            {doctors.length > 0 ? (
              <Select
                selectedPerson={selectedDoctor}
                setSelectedPerson={setSelectedDoctor}
                datas={doctors}
              >
                <div className='flex-btn w-full rounded-lg border border-border p-4 text-sm font-light text-textGray focus:border focus:border-subMain'>
                  {selectedDoctor?.name || "Select Doctor"}{" "}
                  <BiChevronDown className='text-xl' />
                </div>
              </Select>
            ) : (
              <div className='w-full rounded-lg border border-border p-4 text-sm font-light text-textGray'>
                Loading doctors...
              </div>
            )}
          </div>
          <div className='flex w-full flex-col gap-3'>
            <p className='text-sm text-black'>Status</p>
            <Select
              selectedPerson={status}
              setSelectedPerson={setStatus}
              datas={sortsDatas.status}
            >
              <div className='flex-btn w-full rounded-lg border border-border p-4 text-sm font-light text-textGray focus:border focus:border-subMain'>
                {status.name} <BiChevronDown className='text-xl' />
              </div>
            </Select>
          </div>
        </div>

        {/* des */}
        <Textarea
          label='Description'
          placeholder={
            datas?.message
              ? datas.message
              : "She will be coming for a checkup....."
          }
          color={true}
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {/* share */}
        <div className='flex w-full flex-col gap-8'>
          <p className='text-sm text-black'>Share with patient via</p>
          <div className='flex flex-wrap gap-4 sm:flex-nowrap'>
            <Checkbox
              name='email'
              checked={shares.email}
              onChange={onChangeShare}
              label='Email'
            />
            <Checkbox
              name='sms'
              checked={shares.sms}
              onChange={onChangeShare}
              label='SMS'
            />
            <Checkbox
              checked={shares.whatsapp}
              name='whatsapp'
              onChange={onChangeShare}
              label='WhatsApp'
            />
          </div>
        </div>
        {/* buttones */}
        <div className='grid w-full gap-4 sm:grid-cols-2'>
          <button
            onClick={closeModal}
            className='rounded-lg bg-red-600 bg-opacity-5 p-4 text-sm font-light text-red-600'
          >
            {datas?.title ? "Discard" : "Cancel"}
          </button>
          <Button
            label={isLoading ? "Saving..." : "Save"}
            Icon={HiOutlineCheckCircle}
            onClick={handleSave}
            disabled={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
}

export default AddAppointmentModal;
